// ADR 7.10.26v §6 — the sink half of `analytics.ts`, moved verbatim so that
// file has room for the spread. `analytics.ts` re-exports every name here, so
// no importer changed.
//
// THE EVENT NAMES CARRY THE CLIENT, AND ONLY HERE. `posthog-iac` silently drops
// event-property filters and a simple trend has one breakdown axis, which on
// this dashboard is already spent on the dimension being measured. So a web
// `app_opened` would mix two populations into 6.9.26b's panels with no lever to
// separate them. The divergence is a mechanical `web_` prefix and nothing else;
// the *properties* are 6.9.26b's and 6.9.26i's spelled identically, because the
// payoff is a reader comparing like with like.
//
// The sink is injected, defaulting to `capture()` — 6.9.26l §1 and 6.9.26o §1's
// shape, and what lets a vitest drive these with a spy and no SDK.

import { attached, capture } from '../telemetry/capture'

/**
 * Where an event goes, and **whether it landed**.
 *
 * The boolean is §2's launch race, settled: 6.9.26c constructs PostHog behind a
 * dynamic `import()` and deliberately does not make render wait, so the shell's
 * first-mount effect runs before the SDK exists. A latch set on the *call* would
 * make that loss permanent and leave this ADR's two headline panels empty
 * forever. A latch set on *delivery* cannot — see `emitWindowObservation`.
 */
export type LayoutSink = (name: string, properties: Record<string, unknown>) => boolean

/**
 * The four names, as a closed table: a fifth event is an edit here, in sight.
 *
 * `notFound` is ADR 9.9.26b §1 and is the one that is *not* shell-driven — it
 * comes from `app/NotFoundPage.tsx`, not from `AdaptiveShell`'s effect. It lives
 * here anyway because the `web_` prefix rule above is the whole reason this
 * table is a table, and a second module holding a second copy of that rule is
 * how the rule stops being followed.
 */
export const LAYOUT_EVENTS = {
  launch: 'web_app_opened',
  classChanged: 'web_window_size_class_changed',
  planViewed: 'web_lesson_plan_viewed',
  notFound: 'web_route_not_found',
} as const

/**
 * The production sink.
 *
 * `attached()` is 6.9.26c's own test seam and is *read* here, never written:
 * `capture()` is a no-op with no PostHog instance, and that no-op is exactly
 * what this has to be able to tell apart from a delivery.
 */
const defaultSink: LayoutSink = (name, properties) => {
  capture(name, properties)
  return attached().analytics
}

let sink: LayoutSink = defaultSink

/** Test seam: watch the events without attaching an SDK. */
export function setLayoutSink(replacement: LayoutSink): void {
  sink = replacement
}

/** Put 6.9.26c's `capture()` back — the production path. */
export function resetLayoutSink(): void {
  sink = defaultSink
}

/** Send one layout event through the current sink; true when it landed. */
export function sendLayoutEvent(name: string, properties: Record<string, unknown>): boolean {
  return sink(name, properties)
}
